import React, { useEffect, useState } from 'react';
import { ArrowLeft, PencilSimple, Plus, Power, Trash, X } from '@phosphor-icons/react';
import { autoOptimizeConfigService } from '../services/autoOptimizeConfigService.js';
import ActionMenu from '../../../shared/components/ui/ActionMenu.jsx';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog.jsx';

// Índice alineado con backend: 0=Lunes ... 6=Domingo
const DAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const STRATEGY_LABELS = {
  punctuality: 'Puntualidad',
  balanced: 'Balanceado',
  efficiency: 'Eficiencia'
};

const DEFAULT_FORM_VALUES = { day: 0, hour: 8, teams: [], strategy: 'balanced' };

const buildTeamNameById = (teams = []) => teams.reduce((accumulator, team) => {
  accumulator[team.id] = team.name;
  return accumulator;
}, {});

const formatDateTime = (isoDate) => {
  const parsed = new Date(isoDate || '');
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString('es-AR');
};

const getConfigRowKey = (config) => (
  config.teamsKey ? `${config.day}-${config.teamsKey}` : `${config.day}-${config.hour}`
);

const isValidHour = (hour) => hour !== '' && !Number.isNaN(Number(hour));

const TaskDistributionAutomateModal = ({ isOpen, onClose, teams = [] }) => {
  const [view, setView] = useState('list');
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingConfig, setEditingConfig] = useState(null);
  const [editValues, setEditValues] = useState({ hour: 0, strategy: 'balanced' });
  const [editError, setEditError] = useState('');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [pendingDeleteConfig, setPendingDeleteConfig] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [busyRowKey, setBusyRowKey] = useState(null);
  const [rowActionError, setRowActionError] = useState('');

  const teamNameById = buildTeamNameById(teams);

  const loadConfigs = async () => {
    setIsLoading(true);
    setLoadError('');

    try {
      const responses = await autoOptimizeConfigService.getConfigurations();
      setConfigs(responses);
    } catch (error) {
      setLoadError(error.message || 'No se pudieron cargar las automatizaciones.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setRowActionError('');
    loadConfigs();
  }, [isOpen]);

  if (!isOpen) return null;

  const openCreateView = () => {
    setFormValues(DEFAULT_FORM_VALUES);
    setFormError('');
    setView('create');
  };

  const openEditView = (config) => {
    setEditingConfig(config);
    setEditValues({ hour: config.hour, strategy: config.strategy });
    setEditError('');
    setView('edit');
  };

  const toggleTeam = (teamId) => {
    setFormValues((previous) => ({
      ...previous,
      teams: previous.teams.includes(teamId)
        ? previous.teams.filter((id) => id !== teamId)
        : [...previous.teams, teamId]
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isValidHour(formValues.hour)) {
      setFormError('Ingresa una hora válida entre 0 y 23.');
      return;
    }

    if (!formValues.teams.length) {
      setFormError('Selecciona al menos un equipo.');
      return;
    }

    setFormError('');
    setIsSubmitting(true);

    try {
      await autoOptimizeConfigService.createConfiguration({
        day: Number(formValues.day),
        hour: Number(formValues.hour),
        teams: formValues.teams,
        strategy: formValues.strategy
      });
      await loadConfigs();
      setView('list');
    } catch (error) {
      setFormError(error.message || 'No se pudo crear la automatización.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!isValidHour(editValues.hour)) {
      setEditError('Ingresa una hora válida entre 0 y 23.');
      return;
    }

    setEditError('');
    setIsEditSubmitting(true);

    try {
      await autoOptimizeConfigService.updateConfiguration(editingConfig.day, editingConfig.teamsKey, {
        hour: Number(editValues.hour),
        strategy: editValues.strategy
      });
      await loadConfigs();
      setEditingConfig(null);
      setView('list');
    } catch (error) {
      setEditError(error.message || 'No se pudo actualizar la automatización.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleToggleActive = async (config) => {
    const rowKey = getConfigRowKey(config);
    setRowActionError('');
    setBusyRowKey(rowKey);

    try {
      await autoOptimizeConfigService.updateConfiguration(config.day, config.teamsKey, {
        isActive: !config.isActive
      });
      await loadConfigs();
    } catch (error) {
      setRowActionError(error.message || 'No se pudo actualizar el estado de la automatización.');
    } finally {
      setBusyRowKey(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteConfig) return;

    setIsDeleting(true);
    setRowActionError('');

    try {
      await autoOptimizeConfigService.deleteConfiguration(pendingDeleteConfig.day, pendingDeleteConfig.teamsKey);
      await loadConfigs();
      setPendingDeleteConfig(null);
    } catch (error) {
      setRowActionError(error.message || 'No se pudo eliminar la automatización.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-9998 p-4 !mt-0">
      <div className="bg-surface rounded-lg shadow-xl border border-line w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface z-30 flex items-center justify-between p-6 border-b border-line">
          <div className="flex items-start gap-3">
            {view !== 'list' && (
              <button
                type="button"
                onClick={() => setView('list')}
                className="text-content-muted hover:text-content mt-1"
                aria-label="Volver"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h2 className="text-2xl font-bold text-content">
                {view === 'list' && 'Automatizaciones'}
                {view === 'create' && 'Nueva automatización'}
                {view === 'edit' && 'Editar automatización'}
              </h2>
              <p className="text-sm text-content-muted">
                {view === 'list' && 'Distribuciones automáticas configuradas.'}
                {view === 'create' && 'Define día, hora, equipos y estrategia para la nueva automatización.'}
                {view === 'edit' && 'Modifica la hora y la estrategia de la automatización.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-content-muted hover:text-content text-2xl" type="button" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {view === 'list' && (
            <>
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={openCreateView}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber px-4 py-2 text-sm font-semibold text-on-amber hover:bg-amber-hover transition-colors"
                >
                  <Plus size={16} /> Agregar automatización
                </button>
              </div>

              {isLoading && <p className="text-sm text-content-muted">Cargando...</p>}
              {!isLoading && loadError && <p className="text-sm text-error">{loadError}</p>}
              {!isLoading && rowActionError && <p className="text-sm text-error mb-3">{rowActionError}</p>}
              {!isLoading && !loadError && !configs.length && (
                <p className="text-sm text-content-muted">No hay automatizaciones configuradas.</p>
              )}

              <ul className="space-y-3">
                {configs.map((config, index) => {
                  const lastRunLabel = formatDateTime(config.lastRunAt);
                  const rowKey = getConfigRowKey(config);
                  const isRowBusy = busyRowKey === rowKey;

                  return (
                    <li
                      key={rowKey}
                      className="rounded-lg border border-line bg-surface-2 p-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-content">{DAY_LABELS[config.day]} {String(config.hour).padStart(2, '0')}:00</span>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              config.isActive ? 'bg-success/16 text-success' : 'bg-surface-2 text-content-muted'
                            }`}
                          >
                            {config.isActive ? 'Activa' : 'Inactiva'}
                          </span>
                          <ActionMenu
                            options={[
                              {
                                label: 'Editar',
                                icon: <PencilSimple size={16} />,
                                onClick: () => openEditView(config),
                                disabled: isRowBusy
                              },
                              {
                                label: config.isActive ? 'Desactivar' : 'Activar',
                                icon: <Power size={16} />,
                                onClick: () => handleToggleActive(config),
                                disabled: isRowBusy
                              },
                              {
                                label: 'Eliminar',
                                icon: <Trash size={16} />,
                                onClick: () => setPendingDeleteConfig(config),
                                disabled: isRowBusy
                              }
                            ]}
                          />
                        </div>
                      </div>
                      <p className="text-sm text-content-muted mt-1">
                        Estrategia: {STRATEGY_LABELS[config.strategy] || config.strategy}
                      </p>
                      <p className="text-sm text-content-muted">
                        Equipos: {(config.teams || []).map((teamId) => teamNameById[teamId] || teamId).join(', ')}
                      </p>
                      {config.createdBy && (
                        <p className="text-xs text-content-muted mt-1">Creado por: {config.createdBy}</p>
                      )}
                      {lastRunLabel && (
                        <p className="text-xs text-content-muted mt-1">
                          Última ejecución: {lastRunLabel}
                          {config.lastRunMessage ? ` — ${config.lastRunMessage}` : ''}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {view === 'create' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && <p className="text-sm text-error">{formError}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Día</label>
                  <select
                    value={formValues.day}
                    onChange={(event) => setFormValues((previous) => ({ ...previous, day: event.target.value }))}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line focus:outline-none focus:ring-2 focus:ring-amber"
                  >
                    {DAY_LABELS.map((label, index) => (
                      <option key={label} value={index}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-content mb-2">Hora</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={formValues.hour}
                    onChange={(event) => {
                      const rawValue = event.target.value;
                      if (rawValue === '') {
                        setFormValues((previous) => ({ ...previous, hour: '' }));
                        return;
                      }
                      const clampedValue = Math.min(23, Math.max(0, Number(rawValue)));
                      setFormValues((previous) => ({ ...previous, hour: clampedValue }));
                    }}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line focus:outline-none focus:ring-2 focus:ring-amber"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content mb-2">Estrategia</label>
                  <select
                    value={formValues.strategy}
                    onChange={(event) => setFormValues((previous) => ({ ...previous, strategy: event.target.value }))}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line focus:outline-none focus:ring-2 focus:ring-amber"
                  >
                    {Object.entries(STRATEGY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-2">Equipos</label>
                <div className="max-h-48 overflow-y-auto rounded-md border border-line bg-bg/50 p-2 space-y-1">
                  {teams.map((team) => (
                    <label
                      key={team.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-2 text-sm text-content"
                    >
                      <input
                        type="checkbox"
                        checked={formValues.teams.includes(team.id)}
                        onChange={() => toggleTeam(team.id)}
                        className="h-4 w-4 rounded border-line bg-surface-2 text-amber focus:ring-amber"
                      />
                      {team.name}
                    </label>
                  ))}
                  {!teams.length && <p className="text-sm text-content-muted px-2 py-1">No hay equipos disponibles.</p>}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-line">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    isSubmitting ? 'bg-surface-2 text-content-muted cursor-not-allowed' : 'bg-amber text-on-amber hover:bg-amber-hover'
                  }`}
                >
                  {isSubmitting ? 'Guardando...' : 'Crear automatización'}
                </button>
              </div>
            </form>
          )}

          {view === 'edit' && editingConfig && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {editError && <p className="text-sm text-error">{editError}</p>}

              <div className="rounded-md border border-line bg-bg/50 p-3 text-sm text-content-muted space-y-1">
                <p>Día: {DAY_LABELS[editingConfig.day]}</p>
                <p>Equipos: {(editingConfig.teams || []).map((teamId) => teamNameById[teamId] || teamId).join(', ')}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-2">Hora</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={editValues.hour}
                    onChange={(event) => {
                      const rawValue = event.target.value;
                      if (rawValue === '') {
                        setEditValues((previous) => ({ ...previous, hour: '' }));
                        return;
                      }
                      const clampedValue = Math.min(23, Math.max(0, Number(rawValue)));
                      setEditValues((previous) => ({ ...previous, hour: clampedValue }));
                    }}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line focus:outline-none focus:ring-2 focus:ring-amber"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-content mb-2">Estrategia</label>
                  <select
                    value={editValues.strategy}
                    onChange={(event) => setEditValues((previous) => ({ ...previous, strategy: event.target.value }))}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content text-sm border border-line focus:outline-none focus:ring-2 focus:ring-amber"
                  >
                    {Object.entries(STRATEGY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-line">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isEditSubmitting}
                  className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                    isEditSubmitting ? 'bg-surface-2 text-content-muted cursor-not-allowed' : 'bg-amber text-on-amber hover:bg-amber-hover'
                  }`}
                >
                  {isEditSubmitting ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteConfig)}
        title="Eliminar automatización"
        message={pendingDeleteConfig ? `Se eliminará la automatización de ${DAY_LABELS[pendingDeleteConfig.day]} a las ${String(pendingDeleteConfig.hour).padStart(2, '0')}:00. Esta acción no se puede deshacer. ¿Quieres continuar?` : ''}
        confirmText={isDeleting ? 'Eliminando...' : 'Eliminar'}
        cancelText="Cancelar"
        variant="danger"
        onCancel={() => {
          if (!isDeleting) {
            setPendingDeleteConfig(null);
          }
        }}
        onConfirm={() => {
          if (!isDeleting) {
            handleDeleteConfirm();
          }
        }}
      />
    </div>
  );
};

export default TaskDistributionAutomateModal;
